/**
 * Sentry Error Tracking — Merkezi hata yakalama ve raporlama
 * Tüm uncaught exception ve unhandled rejection'lar otomatik yakalanır.
 */
import * as Sentry from '@sentry/react'
import { OTURUM_KAYDI_AYARLARI } from '@/lib/clientConfig'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''

// Yeni ekleme — Rate limiting ile event bütçe koruması
const recentErrors = new Map<string, number>()
const RATE_LIMIT_MS = 60_000  // 1 dakika

function shouldReport(key: string): boolean {
  const now = Date.now()
  const last = recentErrors.get(key) || 0
  // Eğer son gönderme üzerinden 1dk geçmediyse gönderme
  if (now - last < RATE_LIMIT_MS) return false
  recentErrors.set(key, now)
  // Temizlik — 100'den fazla key birikmesin
  if (recentErrors.size > 100) {
    const cutoff = now - RATE_LIMIT_MS
    for (const [k, v] of recentErrors) if (v < cutoff) recentErrors.delete(k)
  }
  return true
}

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('[Sentry] DSN tanımlı değil — hata takibi devre dışı')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'production' | 'development'
    release: `gunum-var@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Performance: Sadece production'da
    // Mevcut rate: 0.2 (%20), Önerilen rate: 0.1 (%10)
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Replay: Hata anındaki kullanıcı hareketlerini kaydet
    replaysSessionSampleRate: 0,      // Normal oturumlarda kayıt yok
    replaysOnErrorSampleRate: 0.3,    // Hata olduğunda %30 kayıt (Mevcut: %50 idi)

    // PII temizleme ve Bütçe Koruması
    beforeSend(event) {
      // Bütçe koruması: Development'ta Sentry'ye gönderme
      if (import.meta.env.DEV) return null

      // Kullanıcı emailini gizle (PII)
      if (event.user?.email) {
        event.user.email = '[REDACTED]'
      }

      // Rate-limit: Aynı hata tekrar tekrar gelmesin
      // Hatanın değerini veya mesajını key olarak kullanıyoruz
      const errKey = event.exception?.values?.[0]?.value || event.message || ''
      if (errKey && !shouldReport(errKey)) return null

      return event
    },

    // Gereksiz hataları filtrele
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Network request failed',
      'Load failed',
      'AbortError',
      'ChunkLoadError',
      'Acquiring an exclusive Navigator LockManager lock',
      'messaging/unsupported-browser',
      "This browser doesn't support the API's required to use the firebase SDK",
      'Güncelleme kontrolü başarısız',
      'Arka plan canlı kur güncellenemedi',
    ],

    integrations: [
      Sentry.browserTracingIntegration(),
      // Degerler `clientConfig.ts`te; orada testleri var.
      Sentry.replayIntegration({ ...OTURUM_KAYDI_AYARLARI }),
    ],
  })

  console.log('[Sentry] Hata takibi aktif')
}

/**
 * Kullanıcı kimliğini Sentry'ye bağla (login sonrası çağır)
 */
export function setSentryUser(userId: string, extra?: Record<string, unknown>) {
  Sentry.setUser({
    id: userId,
    ...extra,
  })
}

/**
 * Kullanıcı çıkışında Sentry user'ı temizle
 */
export function clearSentryUser() {
  Sentry.setUser(null)
}

/**
 * Custom Sentry Tag ekleme
 */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value)
}

/**
 * Manuel hata yakalama — Edge Function, API hataları vb.
 */
export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) {
    console.error('[Sentry Offline]', error, context)
    return
  }

  // Raw objeleri veya PostgREST (Supabase) hatalarını Türkçe anlaşılır Error nesnesine çevir
  let finalError: Error
  if (error instanceof Error) {
    finalError = error
  } else if (typeof error === 'object' && error !== null) {
    const errObj = error as any
    // Supabase error yapısı
    if (errObj.code || errObj.message || errObj.details) {
      const msg = errObj.message || errObj.details || errObj.code || 'Bilinmeyen Veritabanı Hatası'
      let turkceMesaj = `Veritabanı Hatası: ${msg}`
      // Çeviriler eklenebilir
      if (errObj.code?.includes('PGRST')) turkceMesaj = `API Hatası [${errObj.code}]: İşlem başarısız oldu.`
      if (errObj.status === 504 || errObj.code === '504') turkceMesaj = 'Zaman aşımı (Timeout) - İstek cevap vermedi.'
      if (errObj.status === 400 || errObj.code === '400') turkceMesaj = `Geçersiz İstek: ${msg}`
      // Not: Sentry orjinal nesneyi Extra olarak saklar
      finalError = new Error(turkceMesaj)
    } else {
      let str = ''
      try {
        str = JSON.stringify(errObj)
      } catch (_e) {
        str = String(errObj)
      }
      finalError = new Error(`Sistem Hatası Objesi: ${str.substring(0, 100)}`)
    }
  } else {
    finalError = new Error(String(error || 'Bilinmeyen Bir Hata Oluştu'))
  }

  // Özel yönlendirme: Premium Limit uyarısını Error değil Warning olarak logla
  if (finalError.message.includes('Sosyal ağınız giderek büyüyor')) {
    captureWarning(finalError.message, context)
    return
  }

  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })
    }
    // Eğer objeden Error ürettiysek orjinal objeyi extra içine atalım ki detaylara bakabilelim
    if (!(error instanceof Error)) {
      scope.setExtra('rawErrorObject', error)
    }
    Sentry.captureException(finalError)
  })
}

/**
 * Bilgi seviyesinde mesaj logla
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (!SENTRY_DSN) {
    console.log(`[Sentry Offline] ${level}: ${message}`)
    return
  }
  Sentry.captureMessage(message, level)
}

/**
 * Manuel warning yakalama — console.warn() takviyesi vb.
 */
export function captureWarning(message: string, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) {
    console.warn('[Sentry Offline]', message, context)
    return
  }

  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })
    }
    Sentry.captureMessage(message, 'warning')
  })
}

/**
 * Beklenen bir kullanıcı durumunu (iptal, desteklenmeyen ortam) olay üretmeden
 * iz olarak bırakır. Sonraki gerçek bir hata gelirse bu iz onun yanında görünür.
 */
export function recordBreadcrumb(message: string, data?: Record<string, unknown>) {
  if (!SENTRY_DSN) return
  Sentry.addBreadcrumb({ category: 'app', level: 'info', message, data })
}

export { Sentry }
